import dayjs from 'dayjs';
import axios from 'axios';
import get from 'lodash/get';
import { Form } from 'formik';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Heading } from '@kolking/react-ui';

import * as Sentry from '@sentry/react';

import {
  FormInput,
  FormImagePicker,
  FormLocation,
  FormReCAPTCHA,
  FormTextarea,
  YupFormik,
  FormCheckbox,
  FormSelect,
} from '../../components/Formik';

import { type RiskData, RiskSchema, HOW_DID_YOU_HEAR_OPTIONS } from '../../models';
import { Layout } from '../../components/Layout';
import { useConfig } from '../../components/Config';
import { submitRiskForm } from '../../services';
import { generateAlphaNumericRandom, wordCount } from '../../utils/helpers';

import styles from './styles.module.scss';

const createInitialValues = () => {
  return {
    sampleNumber: generateAlphaNumericRandom(9),
  } as RiskData;
};

const initialValues = import.meta.env.DEV
  ? {
      ...createInitialValues(),
      observationDate: dayjs().format('YYYY-MM-DD'),
      location: '-16.193775,143.217285',
      description: 'description',
      hasSampleNumber: true,
      firstName: 'John',
      lastName: 'Smith',
      contactEmail: 'john.smith@google.com',
      contactPhone: '123456',
    }
  : createInitialValues();

const getErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    switch (error.status) {
      case 400: {
        const message = get(error.response, ['data', 'error']) as unknown;

        if (message && typeof message === 'string') {
          return message;
        }

        break;
      }

      case 429:
        return 'Too many requests. Please try again later.';
    }
  } else if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Server error. Please try again later.';
};

const HomePage = () => {
  const config = useConfig();
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: async (values: RiskData) => submitRiskForm(config, values),
  });

  return (
    <Layout>
      <Heading as="h1" title="Report a pest, disease or weed in northern Australia" />
      <YupFormik
        initialValues={initialValues}
        validationSchema={RiskSchema}
        isDisabled={mutation.status === 'pending'}
        onSubmit={(values, { setStatus }) =>
          mutation.mutateAsync(values, {
            onSuccess: () => {
              navigate('/success');
            },
            onError: (error) => {
              setStatus(getErrorMessage(error));
            },
          })
        }
      >
        {({ values, status }) => (
          <Form className={styles.form} noValidate>
            <div className={styles.columns}>
              <fieldset className={styles.fields}>
                <Heading as="h2" title="Your observation" />
                <FormInput
                  type="date"
                  name="observationDate"
                  help="Date must be current or in the past."
                />
                <FormLocation
                  name="location"
                  help="Pin the location using Google Maps. The location must be in the indicated Northern Australia area."
                />
                <FormCheckbox
                  name="hasSampleNumber"
                  label="Send a sample to the Department of Agriculture, Fisheries and Forestry for testing and/or identification"
                />
                {values.hasSampleNumber && (
                  <div className={styles.sample}>
                    <code aria-label="sample number">{values.sampleNumber}</code>
                    <p>Write this sample number on the sample container and send to:</p>
                    <address>
                      Department of Agriculture, Fisheries, and Forestry <br />
                      Attention: community engagement <br />
                      114 Catalina Crescent <br />
                      Airport Business Park <br />
                      Cairns International Airport <br />
                      Cairns QLD 4870
                    </address>
                  </div>
                )}
                <FormTextarea
                  minRows={5}
                  className={styles.description}
                  name="description"
                  help="Please enter a description of your observation in 200 words or less."
                >
                  <small className={styles.words}>{wordCount(values.description)}/200</small>
                </FormTextarea>
                <FormImagePicker
                  name="images"
                  help="You can add up to five photos of your observation."
                />
                <FormSelect name="howDidYouHearAboutUs">
                  <option value="">Please select...</option>
                  {HOW_DID_YOU_HEAR_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </FormSelect>
              </fieldset>
              <hr className={styles.divider} />
              <fieldset className={styles.fields}>
                <Heading as="h2" title="Your contact details" />
                <FormInput name="firstName" />
                <FormInput name="lastName" />
                <FormInput name="contactEmail" />
                <FormInput name="contactPhone" />
                <div className={styles.privacy}>
                  <Heading as="h3" title="Privacy notice" />
                  <p>
                    Personal information means information or an opinion about an identified
                    individual, or an individual who is reasonably identifiable.
                  </p>
                  <p>
                    The Department of Agriculture, Fisheries, and Forestry collects your personal
                    information through this web form for the purposes of enabling us to contact you
                    about your report and related purposes. If you do not provide the relevant
                    personal information requested in this web form, the department will be unable
                    to contact you about your report.
                  </p>
                  <p>
                    The department may disclose your personal information to other Australian
                    Government agencies, persons or organisations where necessary for the purposes
                    described, provided the disclosure is consistent with relevant laws, in
                    particular the Privacy Act 1988. Your personal information will be used and
                    stored in accordance with the Australian Privacy Principles. See our{' '}
                    <a
                      href="https://www.agriculture.gov.au/about/commitment/privacy"
                      target="_blank"
                    >
                      Privacy Policy
                    </a>{' '}
                    web page to learn more about accessing or correcting personal information or
                    making a complaint. Alternatively, telephone the department on +61 2 6272 3933.
                  </p>
                </div>
              </fieldset>
            </div>
            <hr className={styles.divider} />
            <FormReCAPTCHA
              name="captcha"
              sitekey={config.SITEKEY}
              help="The CAPTCHA question is for testing whether or not you are a human visitor and to
              prevent automated spam submissions."
            />
            <hr className={styles.divider} />
            <section className={styles.formfooter}>
              {Boolean(status) && (
                <div className={styles.error}>
                  <b>Failed to submit:</b> {status}
                </div>
              )}
              <div className={styles.buttons}>
                <Button
                  type="reset"
                  size="lg"
                  variant="secondary"
                  title="Cancel"
                  disabled={mutation.status === 'pending'}
                />
                <Button
                  type="submit"
                  size="lg"
                  title="Submit"
                  busy={mutation.status === 'pending'}
                />
              </div>
            </section>
            {import.meta.env.DEV && (
              <pre className={styles.debug}>
                This section won't be shown in production{'\n'}
                {JSON.stringify(values, undefined, 2)}
              </pre>
            )}
            {config.SENTRY_ENVIRONMENT == 'integration' && (
              <Button
                type="button"
                title="Capture Exception"
                onClick={() => {
                  alert(Sentry.captureException(new Error('New Exception')));
                }}
              />
            )}
          </Form>
        )}
      </YupFormik>
    </Layout>
  );
};

export default HomePage;
